/*
  Ported from src/hooks/useCookieConsent.ts + src/components/CookieConsentBanner.tsx.
  CNIL requirement respected deliberately: "Refuser tout" and "Accepter tout"
  are the same size, same visual weight, same number of clicks — no dark
  pattern. Additionally bridges to Shopify's native Customer Privacy API
  (window.Shopify.customerPrivacy) so Shopify's own scripts/pixels — including
  the Google & YouTube app's Google Ads tag, which reads this same API for
  its own consent-mode gating — stay correctly informed of the real choice.

  Bug fixed 2026-09-29: `window.Shopify.customerPrivacy` is NOT present on
  page load. It's a lazy-loaded module — Shopify's own documented pattern
  requires calling `window.Shopify.loadFeatures([{name:'consent-tracking-api',
  version:'0.1'}], callback)` first; only inside that callback does
  `window.Shopify.customerPrivacy` (and `setTrackingConsent`) start to exist.
  Confirmed live: `window.Shopify.customerPrivacy` was still undefined 6+
  seconds after load and at the moment the banner became clickable — this
  file never called `loadFeatures`, so the old `if (window.Shopify &&
  window.Shopify.customerPrivacy && ...)` guard silently failed on every
  single visit, for every visitor, always. The user's choice was saved to
  localStorage (the banner itself worked fine) but NEVER reached Shopify —
  meaning every pixel/app gating on that API (Google Ads among them) stayed
  stuck at "no consent" forever, regardless of what anyone clicked. This is
  almost certainly why Google Ads conversions were never recorded — not a
  Google Ads-specific wiring problem, the whole bridge to Shopify was dead.
*/
(function () {
  var STORAGE_KEY = 'oblivion-cookie-consent-v1';
  var privacyApiReady = false;
  var pendingConsent = null;

  function readStoredConsent() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeStoredConsent(next) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (e) {
      // localStorage unavailable — consent still applies for this page view.
    }
  }

  function sendToShopify(consent) {
    window.Shopify.customerPrivacy.setTrackingConsent(
      {
        analytics: !!consent.analytics,
        marketing: !!consent.marketing,
        preferences: true,
        sale_of_data: !!consent.marketing,
      },
      function () {}
    );
  }

  function bridgeToShopify(consent) {
    if (privacyApiReady) {
      sendToShopify(consent);
    } else {
      // API not loaded yet — loadFeatures() below is asynchronous, and a
      // visitor can click the banner before it resolves. Remember the
      // latest choice instead of dropping it; the loadFeatures callback
      // flushes it as soon as the API becomes available.
      pendingConsent = consent;
    }
  }

  (function initPrivacyApi() {
    if (!window.Shopify || !window.Shopify.loadFeatures) return;
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      function (error) {
        if (error) return;
        privacyApiReady = true;
        if (pendingConsent) {
          sendToShopify(pendingConsent);
          pendingConsent = null;
        }
      }
    );
  })();

  function init(root) {
    var mainView = root.querySelector('[data-cookie-main]');
    var customizeView = root.querySelector('[data-cookie-customize]');
    var analyticsCheckbox = root.querySelector('[data-cookie-analytics]');
    var marketingCheckbox = root.querySelector('[data-cookie-marketing]');

    var existing = readStoredConsent();
    if (existing) {
      root.hidden = true;
      bridgeToShopify(existing);
      return;
    }
    root.hidden = false;

    function respond(consent) {
      writeStoredConsent(consent);
      bridgeToShopify(consent);
      root.hidden = true;
    }

    root.querySelector('[data-cookie-customize-open]').addEventListener('click', function () {
      mainView.hidden = true;
      customizeView.hidden = false;
    });
    root.querySelector('[data-cookie-customize-back]').addEventListener('click', function () {
      customizeView.hidden = true;
      mainView.hidden = false;
    });
    root.querySelector('[data-cookie-refuse-all]').addEventListener('click', function () {
      respond({ analytics: false, marketing: false });
    });
    root.querySelector('[data-cookie-accept-all]').addEventListener('click', function () {
      respond({ analytics: true, marketing: true });
    });
    root.querySelector('[data-cookie-confirm]').addEventListener('click', function () {
      respond({ analytics: analyticsCheckbox.checked, marketing: marketingCheckbox.checked });
    });
  }

  document.querySelectorAll('[data-oblivion-cookie-consent]').forEach(init);
})();
